<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useProgressStore, PLAYABLE_LEVEL_COUNT } from "@/stores/progressStore.js";
import { useScenicCollectionStore } from "@/stores/scenicCollectionStore.js";
import { useSessionStore } from "@/stores/sessionStore.js";
import { gameRuntimeBridge } from "@/game/bridge/runtimeBridge.js";
import {
  SYSTEM_SPECIAL_LEVEL,
  getScenicLevelForPiece
} from "@/data/systemScenicMaps.js";

const router = useRouter();
const languageStore = useLanguageStore();
const progressStore = useProgressStore();
const scenicStore = useScenicCollectionStore();
const sessionStore = useSessionStore();

const startingLevel = ref(null);
function piecesForScenic(
  scenic
) {
  const count =
    Math.max(
      0,
      Number(
        scenic?.pieceCount
      )
      || 0
    );

  return Array.from(
    {
      length: count
    },
    (_, index) =>
      index
  );
}

const scenicCards = computed(() => scenicStore.scenicMaps.map(scenic => ({
  ...scenic,
  collected: scenicStore.collectedCount(scenic),
  complete: scenicStore.isComplete(scenic)
})));

function scenicDisplayName(scenic) {
  const code = String(
    scenic?.mapCode
    ?? scenic?.code
    ?? scenic?.id
    ?? ""
  ).toLowerCase();

  const rawName = String(
    scenic?.name
    ?? ""
  ).trim();

  if (
    rawName === "\u6545\u5BAB"
    || rawName === "\u6545\u5BAE"
    || code.includes("forbidden-city")
    || code.includes("forbidden_city")
    || code.includes("forbiddencity")
    || code.includes("gugong")
  ) {
    return languageStore.t("scenicForbiddenCity");
  }

  if (
    rawName === "\u5929\u575B"
    || rawName === "\u5929\u58C7"
    || code.includes("temple-of-heaven")
    || code.includes("temple_of_heaven")
    || code.includes("templeofheaven")
    || code.includes("tiantan")
  ) {
    return languageStore.t("scenicTempleOfHeaven");
  }

  if (
    rawName === "\u957F\u57CE"
    || rawName === "\u9577\u57CE"
    || code.includes("great-wall")
    || code.includes("great_wall")
    || code.includes("greatwall")
    || code.includes("changcheng")
  ) {
    return languageStore.t("scenicGreatWall");
  }

  if (
    rawName === "\u5F20\u5BB6\u754C"
    || rawName === "\u5F35\u5BB6\u754C"
    || code.includes("zhangjiajie")
  ) {
    return languageStore.t("scenicZhangjiajie");
  }

  return rawName || String(
    scenic?.mapCode
    ?? scenic?.id
    ?? ""
  );
}

function pieceStyle(scenic, index) {
  const column = index % scenic.columns;
  const row = Math.floor(index / scenic.columns);
  const x = scenic.columns <= 1 ? 0 : (column / (scenic.columns - 1)) * 100;
  const y = scenic.rows <= 1 ? 0 : (row / (scenic.rows - 1)) * 100;
  return {
    left: `${(column / scenic.columns) * 100}%`,
    top: `${(row / scenic.rows) * 100}%`,
    width: `${100 / scenic.columns}%`,
    height: `${100 / scenic.rows}%`,
    backgroundImage: `url(${scenic.image})`,
    backgroundSize: `${scenic.columns * 100}% ${scenic.rows * 100}%`,
    backgroundPosition: `${x}% ${y}%`
  };
}

function pieceLevel(scenic, index) {
  return getScenicLevelForPiece(scenic, index);
}

function getEntryLevel(
  scenic
) {
  const levels =
    Array.isArray(
      scenic?.fragments
    )
      ? scenic.fragments
          .map(
            fragment =>
              Number(
                fragment.level
              )
          )
          .filter(
            level =>
              Number.isFinite(
                level
              )
              && level > 0
          )
          .sort(
            (
              left,
              right
            ) =>
              left - right
          )
      : [];

  const missingUnlocked =
    levels.find(
      level =>
        level
          <= PLAYABLE_LEVEL_COUNT
        && progressStore
          .isUnlocked(level)
        && !scenicStore
          .hasFragment(level)
    );

  if (missingUnlocked) {
    return missingUnlocked;
  }

  const unlocked =
    levels.filter(
      level =>
        level
          <= PLAYABLE_LEVEL_COUNT
        && progressStore
          .isUnlocked(level)
    );

  return (
    unlocked.at(-1)
    ?? null
  );
}

function entryLabel(scenic) {
  const entryLevel = getEntryLevel(scenic);
  if (!entryLevel) return languageStore.t("levelLocked");
  return "收集碎片";
}

function handlePieceClick(scenic, piece) {
  const level = pieceLevel(scenic, piece);
  if (!level) return;
  openLevel(level);
}

async function openLevel(level) {
  if (startingLevel.value || !level || !progressStore.isUnlocked(level) || level > PLAYABLE_LEVEL_COUNT) return;
  startingLevel.value = level;
  try {
    if (sessionStore.backendEnabled) {
      const play = await sessionStore.startHistory(level);
      const resolvedLevel = Number(play?.level_no) || level;
      const preloaded = gameRuntimeBridge.isReady()
        ? await gameRuntimeBridge.startLevel(resolvedLevel, { consumeEnergy: false })
        : false;
      await router.push({
        name: "game",
        params: { level: resolvedLevel },
        query: { entry: "history", ...(preloaded ? { preloaded: "1" } : {}) }
      });
    } else {
      const preloaded = gameRuntimeBridge.isReady()
        ? await gameRuntimeBridge.startLevel(level, { consumeEnergy: false })
        : false;
      await router.push({
        name: "game",
        params: { level },
        query: { entry: "history", ...(preloaded ? { preloaded: "1" } : {}) }
      });
    }
  } catch (error) {
    console.warn("Unable to start history level.", error);
  } finally {
    startingLevel.value = null;
  }
}

function openScenic(scenic) {
  openLevel(getEntryLevel(scenic));
}

onMounted(() => {
  progressStore.refresh();
  scenicStore.refresh();
});
</script>

<template>
  <section class="scenic-level-page page-card">
    <div class="page-header scenic-header">
      <button class="back-button" type="button" @click="router.push({ name: 'home' })">
        ← {{ languageStore.t('back') }}
      </button>
      <div class="header-copy">
        <h1 class="page-title">{{ languageStore.t('mapTitle') }}</h1>
        
      </div>
      <span class="header-spacer" />
    </div>

    <div class="scenic-list">
      <article
        v-for="scenic in scenicCards"
        :key="scenic.id"
        class="scenic-card"
        :class="{ complete: scenic.complete }"
      >
        <div class="scenic-card-top">
          <h2>{{ scenicDisplayName(scenic) }}</h2>
          <span v-if="scenic.complete" class="complete-badge">{{ languageStore.t("completed") }}</span>
        </div>

        <div class="puzzle-stage" :class="{ complete: scenic.complete }">
          <img class="puzzle-base" :src="scenic.image" :alt="scenicDisplayName(scenic)" />
          <div class="puzzle-shadow" />
          <div
            v-for="piece in piecesForScenic(scenic)"
            :key="piece"
            class="puzzle-piece"
            :class="{ revealed: scenicStore.hasFragment(pieceLevel(scenic, piece)) }"
            :style="pieceStyle(scenic, piece)"
            role="button"
            tabindex="0"
            @click.stop="handlePieceClick(scenic, piece)"
            @keydown.enter.stop="handlePieceClick(scenic, piece)"
          />
          <div v-if="scenic.complete" class="complete-flare">{{ languageStore.t("completed") }}</div>
        </div>

        <div class="scenic-progress-line">
          <span>{{ languageStore.t("collectedFragments", { count: scenic.collected }) }}</span>
          <span v-if="!scenic.complete">{{ languageStore.t("litLevels", { done: scenic.collected, total: scenic.pieceCount }) }}</span>
          <span v-else>{{ languageStore.t("allFragmentsCollected") }}</span>
        </div>

        <button
          v-if="!scenic.complete"
          class="scenic-entry-button"
          type="button"
          :disabled="!getEntryLevel(scenic) || Boolean(startingLevel)"
          @click="openScenic(scenic)"
        >
          {{ startingLevel === getEntryLevel(scenic) ? languageStore.t("loadingLevel") : entryLabel(scenic) }}
        </button>
      </article>


    </div>
  </section>
</template>

<style scoped>
.scenic-level-page {
  padding: 22px;
  min-height: min(900px, calc(100dvh - 48px));
}
.scenic-header { align-items: flex-start; }
.header-copy { text-align: center; min-width: 0; }
.header-copy .page-title { margin-bottom: 5px; }
.header-copy p {
  margin: 0;
  color: rgba(255,255,255,.48);
  font-size: 13px;
}
.header-spacer { width: 82px; }
.scenic-list {
  width: min(100%, 860px);
  margin: 20px auto 0;
  display: grid;
  gap: 20px;
}
.scenic-card {
  border: 1px solid rgba(82,246,232,.2);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(5,31,45,.88), rgba(3,19,34,.92));
  box-shadow: inset 0 0 34px rgba(44,231,220,.035);
}
.scenic-card { padding: 18px; }
.scenic-card.complete {
  border-color: rgba(112,255,225,.62);
  box-shadow: 0 0 24px rgba(72,244,220,.1), inset 0 0 34px rgba(44,231,220,.05);
}
.scenic-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 12px;
}
.scenic-card-top h2,
.special-card h2 { margin: 0; color: #fff; font-size: 25px; }
.complete-badge {
  padding: 6px 10px;
  border: 1px solid rgba(112,255,225,.46);
  border-radius: 999px;
  color: #a9fff6;
  background: rgba(4,53,60,.56);
  font-size: 12px;
  font-weight: 800;
}
.puzzle-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(255,255,255,.09);
  background: #03111e;
}
.puzzle-base {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(.92) brightness(.2) saturate(.45);
  transform: scale(1.01);
}
.puzzle-shadow {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(2,13,27,.08), rgba(1,9,19,.26));
  pointer-events: none;
}
.puzzle-piece {
  position: absolute;
  z-index: 2;
  opacity: 0;
  background-repeat: no-repeat;
  transition: opacity .35s ease, filter .35s ease, transform .16s ease;
  box-shadow: inset 0 0 0 .5px rgba(255,255,255,.08);
  cursor: pointer;
}
.puzzle-piece.revealed {
  opacity: 1;
  filter: saturate(1.08) brightness(1.04);
}
.puzzle-piece:hover,
.puzzle-piece:focus-visible {
  transform: scale(1.04);
  z-index: 3;
  filter: saturate(1.18) brightness(1.12);
  box-shadow: inset 0 0 0 .5px rgba(255,255,255,.18), 0 0 12px rgba(82,246,232,.22);
}
.puzzle-stage.complete .puzzle-piece.revealed { filter: saturate(1.12) brightness(1.08); }
.puzzle-stage.complete .puzzle-piece:hover,
.puzzle-stage.complete .puzzle-piece:focus-visible {
  filter: saturate(1.22) brightness(1.14);
}
.puzzle-stage.complete .puzzle-piece.revealed { filter: saturate(1.12) brightness(1.08); }
.complete-flare {
  position: absolute;
  z-index: 4;
  right: 12px;
  bottom: 12px;
  padding: 7px 11px;
  border: 1px solid rgba(126,255,235,.58);
  border-radius: 999px;
  background: rgba(3,28,38,.78);
  color: #a9fff6;
  font-size: 12px;
  font-weight: 800;
}
.scenic-progress-line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 2px 10px;
  color: rgba(255,255,255,.68);
  font-size: 13px;
}
.scenic-entry-button {
  width: 100%;
  min-height: 50px;
  border: 1px solid rgba(82,246,232,.58);
  border-radius: 15px;
  background: linear-gradient(180deg, rgba(18,111,121,.9), rgba(4,59,73,.96));
  color: #fff;
  font-size: 16px;
  font-weight: 900;
  letter-spacing: .02em;
}
.scenic-entry-button:disabled {
  opacity: .38;
  cursor: not-allowed;
}
.special-card {
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.special-card p { margin: 4px 0 0; color: rgba(255,255,255,.48); font-size: 13px; }
.special-kicker {
  color: rgba(255,218,92,.82);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .16em;
}
.special-card button {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid rgba(82,246,232,.42);
  border-radius: 13px;
  background: rgba(8,68,78,.72);
  color: #fff;
  font-weight: 800;
}
.special-card button:disabled { opacity: .38; }

@media (max-width: 560px) {
  .scenic-level-page {
    padding: 12px;
    min-height: calc(100dvh - 24px);
  }
  .scenic-header {
    display: block;
    margin-bottom: 6px;
  }
  .scenic-header .back-button {
    width: auto;
    margin-bottom: 9px;
    white-space: nowrap;
  }
  .header-copy { text-align: center; padding: 0 2px; }
  .header-copy .page-title { font-size: 27px; }
  .header-copy p { font-size: 12px; }
  .header-spacer { display: none; }
  .scenic-list { margin-top: 12px; gap: 14px; }
  .scenic-card { padding: 12px; border-radius: 18px; }
  .scenic-card-top { margin-bottom: 9px; }
  .scenic-card-top h2 { font-size: 22px; }
  .complete-badge { padding: 5px 8px; font-size: 10px; }
  .puzzle-stage { border-radius: 14px; aspect-ratio: 16 / 9; }
  .scenic-progress-line { margin: 9px 1px 8px; font-size: 12px; }
  .scenic-entry-button { min-height: 47px; border-radius: 13px; font-size: 15px; }
  .special-card { padding: 14px; align-items: flex-end; border-radius: 18px; }
  .special-card h2 { font-size: 18px; }
  .special-card p { max-width: 220px; font-size: 10px; }
  .special-card button { min-height: 42px; padding: 0 12px; font-size: 12px; }
}

@media (max-width: 370px) {
  .special-card { align-items: stretch; flex-direction: column; }
}

/* REMOVE_RED_FRAME_SUBTITLES_V1_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.workshop-subtitle,
.map-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint {
  display: none !important;
}
/* REMOVE_RED_FRAME_SUBTITLES_V1_END */


/* REMOVE_RED_FRAME_REMAINS_V2_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.map-subtitle,
.workshop-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint,
.description,
.description-text,
.description-label,
.map-description,
.map-description-label,
.map-description-text,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.special-level-card,
.special-level,
.special-chapter,
.chapter-endpoint {
  display: none !important;
}
/* REMOVE_RED_FRAME_REMAINS_V2_END */

</style>





